import { createServerFn } from "@tanstack/react-start";
import { getRequestIP, getRequestHeader } from "@tanstack/react-start/server";
import { createHash } from "crypto";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database } from "@/integrations/supabase/types";

const Input = z.object({
  path: z.string().min(1).max(500),
  referrer: z.string().max(500).nullable().optional(),
  session_id: z.string().min(1).max(100),
});

function hashIp(ip: string | null): string | null {
  if (!ip) return null;
  const salt = process.env.IP_HASH_SECRET || process.env.EVENT_SYNC_SECRET || "what-pv-salt";
  return createHash("sha256").update(`${salt}:${ip}`).digest("hex").slice(0, 32);
}

async function resolveUserId(): Promise<string | null> {
  try {
    const auth = getRequestHeader("authorization");
    if (!auth?.startsWith("Bearer ")) return null;
    const token = auth.slice("Bearer ".length).trim();
    if (!token) return null;
    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return null;
    const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });
    const { data, error } = await client.auth.getClaims(token);
    if (error || !data?.claims?.sub) return null;
    return data.claims.sub as string;
  } catch {
    return null;
  }
}

// In-memory IP→country cache (per-isolate). Avoids hitting the geo API on
// every page view from the same visitor. Entries live 24h.
const geoCache = new Map<string, { code: string | null; ts: number }>();
const GEO_TTL_MS = 24 * 60 * 60 * 1000;

function isPrivateIp(ip: string): boolean {
  if (ip === "127.0.0.1" || ip === "::1" || ip === "localhost") return true;
  if (/^10\./.test(ip)) return true;
  if (/^192\.168\./.test(ip)) return true;
  if (/^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(ip)) return true;
  if (/^f[cd]/i.test(ip)) return true; // IPv6 ULA
  return false;
}

async function lookupCountry(ip: string | null): Promise<string | null> {
  if (!ip || isPrivateIp(ip)) return null;
  const cached = geoCache.get(ip);
  if (cached && Date.now() - cached.ts < GEO_TTL_MS) return cached.code;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 1500);
    // ipwho.is — free, no key, returns JSON with country_code
    const res = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}?fields=success,country_code`, {
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    if (!res.ok) {
      geoCache.set(ip, { code: null, ts: Date.now() });
      return null;
    }
    const j = (await res.json()) as { success?: boolean; country_code?: string };
    const code = j?.success && j.country_code ? j.country_code.toUpperCase() : null;
    geoCache.set(ip, { code, ts: Date.now() });
    return code;
  } catch {
    geoCache.set(ip, { code: null, ts: Date.now() });
    return null;
  }
}

export const trackPageView = createServerFn({ method: "POST" })
  .inputValidator((input) => Input.parse(input))
  .handler(async ({ data }) => {
    const ip =
      getRequestHeader("cf-connecting-ip") ??
      getRequestHeader("x-real-ip") ??
      getRequestIP({ xForwardedFor: true }) ??
      null;

    const ip_hash = hashIp(ip);
    const ua = getRequestHeader("user-agent")?.slice(0, 500) ?? null;
    const headerCountry =
      getRequestHeader("cf-ipcountry") ??
      getRequestHeader("x-vercel-ip-country") ??
      getRequestHeader("x-country-code") ??
      null;
    const country = headerCountry ?? (await lookupCountry(ip));
    const user_id = await resolveUserId();

    if (isImpressionPath(data.path)) return { ok: true };

    await supabaseAdmin.from("page_views").insert({
      path: data.path,
      referrer: data.referrer ?? null,
      session_id: data.session_id,
      user_id,
      user_agent: ua,
      ip_hash,
      country: country ? country.slice(0, 8) : null,
    });

    return { ok: true };
  });

export const IMPRESSION_PATH_PREFIX = "/impression/";

export function isImpressionPath(path: string | null | undefined): boolean {
  return typeof path === "string" && path.startsWith(IMPRESSION_PATH_PREFIX);
}

export function impressionPathFor(placeId: string): string {
  return `${IMPRESSION_PATH_PREFIX}${placeId}`;
}

export function placeIdFromImpressionPath(path: string): string | null {
  if (!isImpressionPath(path)) return null;
  const id = path.slice(IMPRESSION_PATH_PREFIX.length).split(/[/?#]/)[0];
  return id || null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isLikelyBot(ua: string | null): boolean {
  if (!ua) return false;
  return /bot|crawl|spider|preview|facebookexternalhit|slackbot|whatsapp|telegram|discord/i.test(ua);
}

const ImpressionInput = z.object({
  place_ids: z.array(z.string().min(1).max(80)).min(1).max(40),
  path: z.string().max(500).optional(),
  session_id: z.string().min(1).max(100),
});

/** Record that listing cards were actually shown. Stored as /impression/<place_id> page_views. */
export const trackPlaceImpressions = createServerFn({ method: "POST" })
  .inputValidator((input) => ImpressionInput.parse(input))
  .handler(async ({ data }) => {
    const ua = getRequestHeader("user-agent")?.slice(0, 500) ?? null;
    if (isLikelyBot(ua)) return { ok: true, recorded: 0 };

    const ip =
      getRequestHeader("cf-connecting-ip") ??
      getRequestHeader("x-real-ip") ??
      getRequestIP({ xForwardedFor: true }) ??
      null;
    const ip_hash = hashIp(ip);
    const headerCountry =
      getRequestHeader("cf-ipcountry") ??
      getRequestHeader("x-vercel-ip-country") ??
      getRequestHeader("x-country-code") ??
      null;

    const raw = Array.from(new Set(data.place_ids.map((s) => s.trim()).filter(Boolean)));
    const uuids = raw.filter((id) => UUID_RE.test(id));
    const slugs = raw.filter((id) => !UUID_RE.test(id));

    let placeIds = [...uuids];
    if (slugs.length) {
      const { data: rows } = await supabaseAdmin.from("places").select("id").in("slug", slugs);
      for (const r of rows ?? []) {
        if (r.id) placeIds.push(r.id as string);
      }
    }
    placeIds = Array.from(new Set(placeIds));
    if (placeIds.length === 0) return { ok: true, recorded: 0 };

    const since = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();
    const { data: existing } = await supabaseAdmin
      .from("page_views")
      .select("path")
      .eq("session_id", data.session_id)
      .like("path", `${IMPRESSION_PATH_PREFIX}%`)
      .gte("created_at", since)
      .limit(500);
    const already = new Set(
      (existing ?? []).map((r) => placeIdFromImpressionPath(r.path)).filter(Boolean) as string[],
    );
    const fresh = placeIds.filter((id) => !already.has(id));
    if (fresh.length === 0) return { ok: true, recorded: 0 };

    const shownOn = (data.path || "").slice(0, 500);
    const rows = fresh.map((id) => ({
      path: impressionPathFor(id),
      referrer: shownOn || null,
      session_id: data.session_id,
      user_agent: ua,
      ip_hash,
      country: headerCountry ? headerCountry.slice(0, 8) : null,
    }));
    await supabaseAdmin.from("page_views").insert(rows);
    return { ok: true, recorded: fresh.length };
  });
